import { useCallback, useEffect, useRef, useState } from 'react'
import { postMemoryQuery } from '@/frontend/components/DigitalWorkers/memory-bank/memoryQuery'
import type { ProjectKnowledgeRecallPayload } from '@/frontend/features/Fde/types'

export default function useProjectObjectKnowledge(projectId: string, query: string | null) {
  const [payload, setPayload] = useState<ProjectKnowledgeRecallPayload | null>(null)
  const [loading, setLoading] = useState(false)
  const request = useRef<AbortController | null>(null)

  const recall = useCallback(async () => {
    if (!query) return

    request.current?.abort()
    const controller = new AbortController()
    request.current = controller
    setLoading(true)
    try {
      const response = await postMemoryQuery<ProjectKnowledgeRecallPayload>(
        '/dashboard/discovery/project_memory/query',
        { project_id: projectId, query, budget: 'mid', max_tokens: 4096 },
        controller.signal,
      )
      if (request.current !== controller) return

      setPayload(response.ok ? response.data : { ...response.data, status: 'error' })
    } catch (error) {
      if (request.current !== controller || (error instanceof DOMException && error.name === 'AbortError')) return
      setPayload({ status: 'error', results: [] })
    } finally {
      if (request.current === controller) setLoading(false)
    }
  }, [projectId, query])

  useEffect(() => {
    setPayload(null)
    void recall()
    return () => request.current?.abort()
  }, [recall])

  return { payload, loading, retry: recall }
}
